import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiPost } from '../services/api';
import '../styles/FormPages.css';

interface DonorForm {
  fullName: string;
  salutation: string;
  organizationCompany: string;
  emailAddress: string;
  phoneNumber: string;
  mailingAddress: string;
  mailingCity: string;
  mailingZipCode: string;
  birthdayMonth: string;
  birthdayDay: string;
  donorType: string;
  priorityLevel: string;
  preferredRecognition: string;
  interests: string[];
}

const DONOR_INTERESTS = [
  'Food Pantry',
  'Baby Closet',
  'Youth Programs',
  'Education Support',
  'Mental Health Services',
  'Housing Assistance',
  'General Operating',
  'Capital Campaign',
];

export function AddDonorPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<DonorForm>({
    fullName: '',
    salutation: '',
    organizationCompany: '',
    emailAddress: '',
    phoneNumber: '',
    mailingAddress: '',
    mailingCity: 'Miami',
    mailingZipCode: '',
    birthdayMonth: '',
    birthdayDay: '',
    donorType: 'Individual',
    priorityLevel: 'Medium',
    preferredRecognition: 'Public',
    interests: [],
  });

  const handleInterestToggle = (interest: string) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest],
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiPost('/donors', formData);

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create donor');
      }

      navigate('/donors');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-page">
      <div className="page-header">
        <h2>Add New Donor</h2>
        <p>Register a new donor in the system</p>
      </div>

      <form onSubmit={handleSubmit} className="intake-form">
        {error && <div className="alert alert-error">{error}</div>}

        <div className="form-section">
          <h3>Donor Information</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="salutation">Salutation</label>
              <select
                id="salutation"
                value={formData.salutation}
                onChange={(e) => setFormData({ ...formData, salutation: e.target.value })}
              >
                <option value="">Select...</option>
                <option value="Mr.">Mr.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Ms.">Ms.</option>
                <option value="Dr.">Dr.</option>
                <option value="Prof.">Prof.</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="fullName">Full Name *</label>
              <input
                type="text"
                id="fullName"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="donorType">Donor Type *</label>
            <select
              id="donorType"
              value={formData.donorType}
              onChange={(e) => setFormData({ ...formData, donorType: e.target.value })}
              required
            >
              <option value="Individual">Individual</option>
              <option value="Corporate">Corporate</option>
              <option value="Foundation">Foundation</option>
            </select>
          </div>

          {formData.donorType !== 'Individual' && (
            <div className="form-group">
              <label htmlFor="organizationCompany">Organization/Company Name</label>
              <input
                type="text"
                id="organizationCompany"
                value={formData.organizationCompany}
                onChange={(e) => setFormData({ ...formData, organizationCompany: e.target.value })}
              />
            </div>
          )}
        </div>

        <div className="form-section">
          <h3>Contact Information</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="emailAddress">Email Address *</label>
              <input
                type="email"
                id="emailAddress"
                value={formData.emailAddress}
                onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number *</label>
              <input
                type="tel"
                id="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                pattern="[0-9]{10}"
                placeholder="3051234567"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="mailingAddress">Street Address</label>
            <input
              type="text"
              id="mailingAddress"
              value={formData.mailingAddress}
              onChange={(e) => setFormData({ ...formData, mailingAddress: e.target.value })}
              placeholder="123 Main Street"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="mailingCity">City</label>
              <input
                type="text"
                id="mailingCity"
                value={formData.mailingCity}
                onChange={(e) => setFormData({ ...formData, mailingCity: e.target.value })}
                placeholder="Miami"
              />
            </div>

            <div className="form-group">
              <label htmlFor="mailingZipCode">Zip Code</label>
              <input
                type="text"
                id="mailingZipCode"
                value={formData.mailingZipCode}
                onChange={(e) => setFormData({ ...formData, mailingZipCode: e.target.value })}
                pattern="[0-9]{5}"
                placeholder="33127"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Additional Details</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="birthdayMonth">Birthday Month</label>
              <select
                id="birthdayMonth"
                value={formData.birthdayMonth}
                onChange={(e) => setFormData({ ...formData, birthdayMonth: e.target.value })}
              >
                <option value="">Select...</option>
                {[
                  'January',
                  'February',
                  'March',
                  'April',
                  'May',
                  'June',
                  'July',
                  'August',
                  'September',
                  'October',
                  'November',
                  'December',
                ].map((month, i) => (
                  <option key={month} value={String(i + 1)}>
                    {month}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="birthdayDay">Birthday Day</label>
              <select
                id="birthdayDay"
                value={formData.birthdayDay}
                onChange={(e) => setFormData({ ...formData, birthdayDay: e.target.value })}
                disabled={!formData.birthdayMonth}
              >
                <option value="">Select...</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <option key={day} value={String(day)}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="priorityLevel">Priority Level *</label>
              <select
                id="priorityLevel"
                value={formData.priorityLevel}
                onChange={(e) => setFormData({ ...formData, priorityLevel: e.target.value })}
                required
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="preferredRecognition">Preferred Recognition *</label>
              <select
                id="preferredRecognition"
                value={formData.preferredRecognition}
                onChange={(e) => setFormData({ ...formData, preferredRecognition: e.target.value })}
                required
              >
                <option value="Public">Public</option>
                <option value="Anonymous">Anonymous</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Areas of Interest</label>
            <div className="checkbox-group">
              {DONOR_INTERESTS.map((interest) => (
                <label key={interest} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.interests.includes(interest)}
                    onChange={() => handleInterestToggle(interest)}
                  />{' '}
                  {interest}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={() => navigate('/donors')}>
            Cancel
          </button>
          <button type="submit" className="button-primary" disabled={loading}>
            {loading ? 'Adding Donor...' : 'Add Donor'}
          </button>
        </div>
      </form>
    </div>
  );
}
